import React, { useState, useEffect, useRef } from 'react';

const BROADCAST_DURATION = 30;

/**
 * BroadcastModal — shown to driver when a new order is broadcast via Supabase Realtime.
 * Props:
 *   order: { id, orderNumber, serviceType, pickupAddress, dropoffAddress, distanceKm,
 *             totalAmount, vehicleType, deliveryDetails }
 *   onAccept: (orderId, driverId) => void
 *   onReject: (orderId, driverId) => void
 *   onDismiss: () => void
 *   driverId: string
 */
const BroadcastModal = ({ order, onAccept, onReject, onDismiss, driverId }) => {
  const [timeLeft, setTimeLeft] = useState(BROADCAST_DURATION);
  const [accepting, setAccepting] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [result, setResult] = useState(null); // 'taken' | 'accepted' | null
  const timerRef = useRef(null);

  useEffect(() => {
    if (timeLeft <= 0) {
      handleTimeout();
      return;
    }
    timerRef.current = setTimeout(() => setTimeLeft(prev => prev - 1), 1000);
    return () => clearTimeout(timerRef?.current);
  }, [timeLeft]);

  const handleTimeout = () => {
    clearTimeout(timerRef?.current);
    onDismiss?.();
  };

  const handleAccept = async () => {
    clearTimeout(timerRef?.current);
    setAccepting(true);
    try {
      await onAccept?.(order?.id, driverId);
    } catch (err) {
      if (err?.message?.includes('already assigned') || err?.message?.includes('expired')) {
        setResult('taken');
        setTimeout(() => onDismiss?.(), 2000);
      }
    } finally {
      setAccepting(false);
    }
  };

  const handleReject = async () => {
    clearTimeout(timerRef?.current);
    setRejecting(true);
    try {
      await onReject?.(order?.id, driverId);
    } finally {
      setRejecting(false);
      onDismiss?.();
    }
  };

  const progress = (timeLeft / BROADCAST_DURATION) * 100;
  const isUrgent = timeLeft <= 8;
  const isDelivery = order?.serviceType === 'delivery';
  const dd = order?.deliveryDetails || {};

  // Animated ring circumference
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  if (result === 'taken') {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
        <div className="w-full max-w-sm bg-[#0a1628] rounded-3xl p-8 text-center border border-white/10 shadow-2xl">
          <div className="w-16 h-16 rounded-full bg-orange-500/20 flex items-center justify-center mx-auto mb-4">
            <svg viewBox="0 0 24 24" fill="#f97316" className="w-8 h-8">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
          </div>
          <h3 className="text-white font-black text-xl mb-2">Order Taken</h3>
          <p className="text-white/50 text-sm">Another driver accepted this order first.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-[#0a1628] rounded-t-3xl sm:rounded-3xl shadow-2xl border border-white/10 overflow-hidden">
        {/* Progress bar */}
        <div className="relative h-1.5 bg-white/10">
          <div
            className={`h-full transition-all duration-1000 ease-linear ${isUrgent ? 'bg-red-500' : 'bg-[#ffd700]'}`}
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="p-5">
          {/* Header row */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="relative w-12 h-12 flex-shrink-0">
                {/* Animated countdown ring */}
                <svg className="w-12 h-12 -rotate-90" viewBox="0 0 84 84">
                  <circle cx="42" cy="42" r={radius} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="6" />
                  <circle
                    cx="42" cy="42" r={radius}
                    fill="none"
                    stroke={isUrgent ? '#ef4444' : '#ffd700'}
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    className="transition-all duration-1000 ease-linear"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className={`font-black text-sm ${isUrgent ? 'text-red-400' : 'text-[#ffd700]'}`}>{timeLeft}</span>
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${isDelivery ? 'bg-blue-500/20 text-blue-400' : 'bg-[#ffd700]/20 text-[#ffd700]'}`}>
                    {isDelivery ? '📦 DELIVERY' : '🚗 RIDE'}
                  </span>
                </div>
                <p className="text-white font-black text-base mt-0.5">New Order Request</p>
                <p className="text-white/40 text-xs">{order?.orderNumber}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[#ffd700] font-black text-2xl">KSh {order?.totalAmount}</p>
              <p className="text-white/40 text-xs">{order?.distanceKm ? `${parseFloat(order?.distanceKm)?.toFixed(1)} km` : ''}</p>
            </div>
          </div>

          {/* Route */}
          <div className="bg-white/5 rounded-2xl p-3.5 mb-3 space-y-2.5">
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-green-500/20 border border-green-500/50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <div className="w-2 h-2 rounded-full bg-green-400" />
              </div>
              <div className="min-w-0">
                <p className="text-white/40 text-[10px] uppercase tracking-wider font-semibold">Pickup</p>
                <p className="text-white text-sm font-semibold leading-tight">{order?.pickupAddress}</p>
              </div>
            </div>
            <div className="ml-2.5 w-px h-3 bg-white/10" />
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-[#ffd700]/20 border border-[#ffd700]/50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <div className="w-2 h-2 rounded-full bg-[#ffd700]" />
              </div>
              <div className="min-w-0">
                <p className="text-white/40 text-[10px] uppercase tracking-wider font-semibold">Drop-off</p>
                <p className="text-white text-sm font-semibold leading-tight">{order?.dropoffAddress}</p>
              </div>
            </div>
          </div>

          {/* Delivery-specific details */}
          {isDelivery && (dd?.recipientName || dd?.packageType || dd?.senderName) && (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-2xl p-3.5 mb-3 space-y-2">
              <p className="text-blue-400 text-xs font-bold uppercase tracking-wider">Delivery Info</p>
              {dd?.packageType && (
                <div className="flex items-center gap-2">
                  <span className="text-white/40 text-xs">Package:</span>
                  <span className="text-white text-xs font-semibold capitalize">{dd?.packageType}</span>
                  {dd?.weight && <span className="text-white/40 text-xs">· {dd?.weight}kg</span>}
                </div>
              )}
              {dd?.senderName && (
                <div className="flex items-center gap-2">
                  <span className="text-white/40 text-xs">Sender:</span>
                  <span className="text-white text-xs font-semibold">{dd?.senderName}</span>
                  {dd?.senderPhone && <span className="text-white/40 text-xs">{dd?.senderPhone}</span>}
                </div>
              )}
              {dd?.recipientName && (
                <div className="flex items-center gap-2">
                  <span className="text-white/40 text-xs">Recipient:</span>
                  <span className="text-white text-xs font-semibold">{dd?.recipientName}</span>
                  {dd?.recipientPhone && <span className="text-white/40 text-xs">{dd?.recipientPhone}</span>}
                </div>
              )}
              {dd?.notes && (
                <div className="flex items-start gap-2">
                  <span className="text-white/40 text-xs flex-shrink-0">Notes:</span>
                  <span className="text-white/70 text-xs">{dd?.notes}</span>
                </div>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleReject}
              disabled={rejecting || accepting}
              className="flex-1 py-4 rounded-2xl border-2 border-red-500/40 text-red-400 font-black text-sm hover:bg-red-500/10 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
              {rejecting ? '...' : 'Reject'}
            </button>
            <button
              onClick={handleAccept}
              disabled={accepting || rejecting}
              className={`flex-[2] py-4 rounded-2xl font-black text-sm transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2 ${
                isUrgent
                  ? 'bg-red-500 hover:bg-red-600 text-white' :'bg-[#ffd700] hover:bg-[#ffed4a] text-[#0a1628]'
              }`}
            >
              {accepting ? (
                <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                  Accept · {timeLeft}s
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BroadcastModal;
